import React, { useEffect, useState, useMemo } from 'react';
import { dashboardAPI, projectsAPI, tasksAPI, usersAPI, notificationsAPI, aiAssistantAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Skeleton, Badge, Button, Empty } from '../components/ui';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, PieChart, Pie, Legend } from 'recharts';
import {
  FolderKanban, CheckSquare, Users, CalendarDays, Target, Sparkles,
  Activity, Zap, CircleDot, ArrowUpRight, Bell,
} from 'lucide-react';
import './Dashboard.css';

/* ── Colour maps ── */
const statusColor = {
  Backlog:       '#e9d5ff',
  'To Do':       '#d8b4fe',
  'In Progress': '#c084fc',
  'In Review':   '#a855f7',
  Done:          '#7e22ce',
};

const activityTypeColors = {
  note:    '#a78bfa',
  call:    '#38bdf8',
  email:   '#06d6a0',
  meeting: '#ffd166',
};

/* ── Stat card definitions ── */
const metricItems = [
  {
    key: 'projects', label: 'Active Projects', icon: FolderKanban,
    cardClass: 'dashboard__stat--violet',
    iconStyle: { background: '#ede9fe', color: '#8b5cf6' },
    glowColor: '#8b5cf6',
    valueColor: '#1f2937',
    trend: 'In delivery',
  },
  {
    key: 'completed', label: 'Completed Tasks', icon: CheckSquare,
    cardClass: 'dashboard__stat--blue',
    iconStyle: { background: '#ede9fe', color: '#8b5cf6' },
    glowColor: '#8b5cf6',
    valueColor: '#1f2937',
    trend: 'Closed this cycle',
  },
  {
    key: 'overdue', label: 'Overdue', icon: CalendarDays,
    cardClass: 'dashboard__stat--amber',
    iconStyle: { background: '#ede9fe', color: '#8b5cf6' },
    glowColor: '#8b5cf6',
    valueColor: '#1f2937',
    trend: 'Needs attention',
  },
  {
    key: 'team', label: 'Team Members', icon: Users,
    cardClass: 'dashboard__stat--green',
    iconStyle: { background: '#ede9fe', color: '#8b5cf6' },
    glowColor: '#8b5cf6',
    valueColor: '#1f2937',
    trend: 'Across workspace',
  },
];

/* ── Hero metrics ── */
const heroMetricIcons = {
  'Open tasks':    Zap,
  'Due soon':      CalendarDays,
  'Completion':    Target,
};
const heroMetricColors = ['#8b84ff', '#06d6a0', '#ffd166'];

/* ── Tooltip ── */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border-bright)',
      borderRadius: 10, padding: '10px 14px', minWidth: 130,
      boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)', fontFamily: 'var(--font-display)' }}>
        {payload[0].value}
      </div>
    </div>
  );
};

/* ── Helpers ── */
const getInitials = (name = '') =>
  name.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const parseMilestones = (milestones) => {
  if (Array.isArray(milestones)) return milestones;
  if (typeof milestones === 'string') {
    try {
      const parsed = JSON.parse(milestones);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

/* ─────────────────────────────────────────── */
import ClientDashboard from './ClientDashboard';

export default function Dashboard() {
  const { user, canCreate } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary]           = useState(null);
  const [projects, setProjects]         = useState([]);
  const [users, setUsers]               = useState([]);
  const [tasksByStatus, setTasksByStatus] = useState([]);
  const [activities, setActivities]     = useState([]);
  const [myTasks, setMyTasks]           = useState([]);
  const [allTasks, setAllTasks]         = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  
  // New Interactive States
  const [timeFilter, setTimeFilter]     = useState('This Week');
  const [projectSearch, setProjectSearch] = useState('');
  const [activeMenu, setActiveMenu]     = useState(null); // 'filters', 'milestone', or task id
  
  // AI Chat States
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState('');

  useEffect(() => {
    // If the user is a client, we don't need to load the admin/manager dashboard data
    if (user?.role === 'Client') return;

    const load = async () => {
      setLoading(true);
      try {
        const [sumRes, statusRes, actRes, projRes, usrRes, myTasksRes, allTasksRes, notificationsRes] =
          await Promise.all([
            dashboardAPI.summary(),
            dashboardAPI.tasksByStatus(),
            dashboardAPI.recentActivities(),
            projectsAPI.list(),
            usersAPI.list().catch(() => ({ data: { data: [] } })),
            user ? tasksAPI.list({ assigned_to: user.user_id }) : Promise.resolve({ data: { data: [] } }),
            tasksAPI.list(),
            notificationsAPI.list().catch(() => ({ data: { data: [] } })),
          ]);

        setSummary(sumRes.data.data);
        setTasksByStatus(statusRes.data.data || []);
        setActivities(actRes.data.data || []);
        setProjects(projRes.data.data || []);
        setUsers(usrRes.data.data || []);
        setMyTasks(myTasksRes.data.data || []);
        setAllTasks(allTasksRes.data.data || []);
        setUnreadNotifications((notificationsRes.data.data || []).filter((n) => !n.read).length);
      } catch (err) {
        console.error(err);
        setError('Failed to load dashboard. Please check that the backend is running.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  if (user?.role === 'Client') {
    return <ClientDashboard />;
  }

  /* ── Derived values ── */
  const overdueCount    = projects.filter(p => p.end_date && new Date(p.end_date) < new Date() && p.status !== 'Done').length;
  const completedTasks  = summary ? Math.max((summary.tasks?.total ?? 0) - (summary.tasks?.open ?? 0), 0) : 0;
  const activeProjects  = projects.filter(p => p.status !== 'Done' && p.status !== 'Backlog');
  const openMyTasks     = myTasks.filter(t => t.status !== 'Done').slice(0, 5);
  const upcomingMilestones = projects
    .filter(p => {
      if (user?.role === 'Admin') return true;
      let mems = [];
      if (Array.isArray(p.members)) mems = p.members.map(Number);
      else try { mems = JSON.parse(p.members || '[]').map(Number); } catch {}
      return mems.includes(Number(user?.user_id)) || Number(p.projectLead?.user_id) === Number(user?.user_id) || Number(p.project_manager_id) === Number(user?.user_id);
    })
    .flatMap(p => parseMilestones(p.milestones).map(m => ({ ...m, project_name: p.project_name })))
    .filter(m => m && m.date)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 4);
  const dueSoonCount = projects.filter((project) => {
    if (!project.end_date || project.status === 'Done') return false;
    const dueDate = new Date(project.end_date);
    const sevenDaysFromNow = new Date();
    sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
    return dueDate >= new Date() && dueDate <= sevenDaysFromNow;
  }).length;
  const projectStatusData = Object.entries(projects.reduce((counts, project) => {
    const status = project.status || 'Backlog';
    counts[status] = (counts[status] || 0) + 1;
    return counts;
  }, {})).map(([status, count]) => ({
    status,
    count,
    fill: statusColor[status] || 'var(--accent)',
  }));

  const taskCountsByUser = users
    .map(u => ({ user: u, count: allTasks.filter(t => t.assigned_to === u.user_id).length }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  const statData = {
    projects:  summary?.projects?.active ?? 0,
    completed: completedTasks,
    overdue:   overdueCount,
    team:      users.length,
  };

  const heroMetrics = [
    { label: 'Open tasks',   value: summary?.tasks?.open ?? 0 },
    { label: 'Due soon',     value: dueSoonCount },
    {
      label: 'Completion',
      value: `${summary?.tasks?.total ? Math.round((completedTasks / summary.tasks.total) * 100) : 0}%`,
    },
  ];

  /* ── Dynamic Chart Data based on Time Filter ── */
  const filterTasksByTime = (tasks) => {
    if (timeFilter === 'All Time') return tasks;
    const now = new Date();
    return tasks.filter(t => {
      // Fallback to start_date or today if created_at doesn't exist
      const d = new Date(t.created_at || t.start_date || new Date());
      if (timeFilter === 'This Week') {
        const diffTime = Math.abs(now - d);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
        return diffDays <= 7;
      }
      if (timeFilter === 'This Month') {
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      }
      if (timeFilter === 'This Year') {
        return d.getFullYear() === now.getFullYear();
      }
      return true;
    });
  };

  const filteredTasks = filterTasksByTime(allTasks);
  const statusCounts = filteredTasks.reduce((acc, t) => {
    acc[t.status] = (acc[t.status] || 0) + 1;
    return acc;
  }, {});
  
  const dynamicChartData = Object.keys(statusCounts).map(status => ({
    status,
    value: statusCounts[status],
    fill: statusColor[status] || '#6c63ff'
  }));
  const finalChartData = dynamicChartData.length > 0 ? dynamicChartData : [{ status: 'No Data', value: 1, fill: 'var(--bg-hover)' }];

  /* ── Derived Search Projects ── */
  const searchedProjects = activeProjects.filter(p => {
    const s = projectSearch.toLowerCase();
    return p.project_name?.toLowerCase().includes(s) ||
           p.project_code?.toLowerCase().includes(s) ||
           p.project_key?.toLowerCase().includes(s) ||
           p.team?.toLowerCase().includes(s) ||
           p.client?.name?.toLowerCase().includes(s);
  }).slice(0, 5);

  const exportCSV = () => {
    const headers = ['Project Name', 'Team', 'Status', 'Due Date'];
    const csvRows = [headers.join(',')];
    projects.forEach(p => {
      const row = [
        `"${p.project_name}"`,
        `"${p.team || ''}"`,
        `"${p.status}"`,
        `"${p.end_date ? new Date(p.end_date).toLocaleDateString() : ''}"`
      ];
      csvRows.push(row.join(','));
    });
    const csvData = csvRows.join('\n');
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', 'projects_export.csv');
    a.click();
  };

  const toggleMenu = (menuId) => {
    setActiveMenu(activeMenu === menuId ? null : menuId);
  };

  const handleChatSubmit = async (e, presetMessage) => {
    if (e) e.preventDefault();
    const question = (presetMessage ?? chatInput).trim();
    if (!question || chatLoading) return;

    const userMessage = { text: question, sender: 'user' };
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setChatError('');
    setChatLoading(true);

    try {
      const res = await aiAssistantAPI.chat(question, chatMessages);
      const reply = res?.data?.data?.reply || "I couldn't find anything related to that in my local library.";
      setChatMessages(prev => [...prev, { text: reply, sender: 'ai' }]);
    } catch (err) {
      const message = 'The AI Assistant is unavailable right now. Please try again in a moment.';
      setChatError(message);
      setChatMessages(prev => [...prev, { text: message, sender: 'ai' }]);
    } finally {
      setChatLoading(false);
    }
  };

  const chatSuggestions = useMemo(() => {
    if (user?.role === 'Admin') {
      return [
        'What tasks are assigned to me?',
        'What deadlines are coming up?',
        'Generate a project status report',
        'Show team workload',
        'Show lead & deal pipeline',
      ];
    }
    if (user?.role === 'Manager') {
      return [
        'What tasks are assigned to me?',
        'What deadlines are coming up?',
        'Generate a project status report',
        'Show team workload',
        'How does task verification work?',
      ];
    }
    return [
      'What tasks are assigned to me?',
      'What deadlines are coming up?',
      'How to submit task for review?',
      'Explain git proof submission',
      'Show high priority tasks',
    ];
  }, [user?.role]);

  const topWorkload   = Math.max(1, taskCountsByUser[0]?.count ?? 1);
  const greeting      = user?.name ? `Welcome Back, ${user.name.split(' ')[0]}.` : 'Welcome Back.';
  const today         = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  /* ── Pipeline bar colors ── */
  const projectAccentColors = ['#8b5cf6', '#a855f7', '#7e22ce', '#c084fc', '#d8b4fe', '#9333ea'];

  return (
    <div className="dashboard-modern animate-in">
      <div className="dashboard-modern__header">
        <div>
          <p className="dashboard-modern__eyebrow">Workspace / Overview</p>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
            <h1 className="dashboard-modern__title">{greeting}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'var(--bg-hover)', padding: '4px 10px', borderRadius: '16px' }}>
              <span style={{ color: '#fbbf24', fontSize: '16px' }}>★</span>
              <span style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text)' }}>{user?.rating || '0.0'}</span>
            </div>
          </div>
          <p className="dashboard-modern__subtitle">{overdueCount ? `${overdueCount} project${overdueCount === 1 ? '' : 's'} need attention this week.` : 'Your project workspace is on track.'}</p>
        </div>
        <div className="dashboard-modern__actions">
          <div className="dashboard-modern__action-menu">
            <Button
              variant="ghost"
              className="dashboard-modern__toolbar-button dashboard-modern__toolbar-button--filter"
              onClick={() => toggleMenu('filters')}
            >
              Filters
            </Button>
            {activeMenu === 'filters' && (
              <div className="dashboard-modern__dropdown">
                <div className="dashboard-modern__dropdown-item">Active Projects Only</div>
                <div className="dashboard-modern__dropdown-item">Assigned to Me</div>
                <div className="dashboard-modern__dropdown-item">High Priority</div>
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            className="dashboard-modern__toolbar-button dashboard-modern__toolbar-button--notification"
            onClick={() => navigate('/app/notifications')}
          >
            <Bell size={18} />
            {unreadNotifications > 0 && (
              <span style={{
                position: 'absolute',
                top: -4,
                right: -4,
                minWidth: 18,
                height: 18,
                borderRadius: 999,
                background: '#ef4444',
                color: '#fff',
                fontSize: 11,
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 5px',
              }}>
                {unreadNotifications > 99 ? '99+' : unreadNotifications}
              </span>
            )}
          </Button>
          <Button className="dashboard-modern__toolbar-button dashboard-modern__toolbar-button--export" onClick={exportCSV}>
            Export CSV
          </Button>
        </div>
      </div>

      <div className="dashboard-modern__ticker" aria-label="Workspace statistics">
        <div className="dashboard-modern__ticker-track">
          {[
            ['Active', statData.projects, 'up'], ['Completed', statData.completed, 'up'],
            ['Overdue', statData.overdue, statData.overdue ? 'down' : 'flat'],
            ['Team online', `${users.length} members`, 'flat'], ['Due soon', dueSoonCount, 'down'],
          ].flatMap((item) => [item, item]).map(([label, value, tone], index) => (
            <div className="dashboard-modern__tick" key={`${label}-${index}`}>
              <span>{label}</span><strong>{loading ? '—' : value}</strong><em className={`dashboard-modern__tick--${tone}`}>{tone === 'up' ? '▲' : tone === 'down' ? '●' : '—'}</em>
            </div>
          ))}
        </div>
      </div>

      <section className="dashboard-modern__stats-row">
        {metricItems.map((item) => {
          const Icon = item.icon;
          return (
            <article key={item.key} className={`dashboard-modern__stat-card glass-panel`}>
              <div className="dashboard-modern__stat-top">
                <div className="dashboard-modern__stat-text">
                  <div className="dashboard-modern__stat-label">{item.label}</div>
                  <div className="dashboard-modern__stat-value">{loading ? '—' : statData[item.key]}</div>
                </div>
                <div className="dashboard-modern__stat-icon">
                  <Icon size={18} />
                </div>
              </div>
              <div className="dashboard-modern__stat-sparkline">
                 {[4,7,3,8,5,9,4,6,3,7,2,5].map((h, i) => (
                   <div key={i} className="dashboard-modern__spark-bar" style={{ height: `${h * 10}%` }} />
                 ))}
              </div>
            </article>
          );
        })}
      </section>

      <section className="dashboard-modern__middle-row">
        {/* Pie Chart */}
        <div className="dashboard-modern__panel">
          <div className="dashboard-modern__panel-header">
            <h2 className="dashboard-modern__panel-title">Task Distribution</h2>
            <select className="dashboard-modern__select" value={timeFilter} onChange={(e) => setTimeFilter(e.target.value)}>
              <option>This Week</option>
              <option>This Month</option>
              <option>This Year</option>
              <option>All Time</option>
            </select>
          </div>
          <div className="dashboard-modern__chart-container">
            {loading ? <Skeleton height={200} /> : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={finalChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    nameKey="status"
                  >
                    {finalChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px' }}/>
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Project status chart */}
        <div className="dashboard-modern__panel">
          <div className="dashboard-modern__panel-header">
            <h2 className="dashboard-modern__panel-title">Project Status</h2>
          </div>
          <div className="dashboard-modern__chart-container">
             {loading ? <Skeleton height={200} /> : projectStatusData.length === 0 ? <Empty message="No project data" /> : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={projectStatusData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-bright)" />
                    <XAxis dataKey="status" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                    <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                    <Tooltip cursor={{ fill: 'var(--accent-surface)' }} contentStyle={{ borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-card)', boxShadow: 'var(--shadow-card)' }} />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]} barSize={40}>
                      {projectStatusData.map((entry, index) => <Cell key={`project-status-${index}`} fill={entry.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
             )}
          </div>
        </div>

        {/* Black Meeting Card */}
        <div className="dashboard-modern__dark-card">
           <div className="dashboard-modern__dark-card-content">
             <div className="dashboard-modern__dark-card-header">
               <h3 className="dashboard-modern__dark-title">Next Major Milestone</h3>
               <div style={{ position: 'relative' }}>
                 <button className="dashboard-modern__dark-more" onClick={() => toggleMenu('milestone')}>•••</button>
                 {activeMenu === 'milestone' && (
                   <div className="dashboard-modern__dropdown dashboard-modern__dropdown--right">
                     <div className="dashboard-modern__dropdown-item" onClick={() => navigate('/app/projects')}>View Project</div>
                     <div className="dashboard-modern__dropdown-item">Mark Complete</div>
                   </div>
                 )}
               </div>
             </div>
             <p className="dashboard-modern__dark-subtitle">
               {upcomingMilestones[0] ? upcomingMilestones[0].name : 'No upcoming milestones'}
             </p>
             <p className="dashboard-modern__dark-time">
               {upcomingMilestones[0] ? formatDate(upcomingMilestones[0].date) : '—'}
             </p>
           </div>
           <button className="dashboard-modern__dark-button" onClick={() => navigate('/app/projects')}>
              View Details
           </button>
        </div>
      </section>

      <section className="dashboard-modern__bottom-row">
        {/* Active Projects Table-like view */}
        <div className="dashboard-modern__panel">
          <div className="dashboard-modern__panel-header">
            <h2 className="dashboard-modern__panel-title">Active Projects</h2>
            <div className="dashboard-modern__search">
              <input type="text" placeholder="Search projects..." value={projectSearch} onChange={(e) => setProjectSearch(e.target.value)} />
            </div>
          </div>
          <div className="dashboard-modern__table-wrap">
             <table className="dashboard-modern__table">
               <thead>
                 <tr>
                   <th>Project Code</th>
                   <th>Project Name</th>
                   <th>Team</th>
                   <th>Status</th>
                   <th>Action</th>
                 </tr>
               </thead>
               <tbody>
                 {loading ? <tr><td colSpan="5"><Skeleton height={40}/></td></tr> : searchedProjects.length === 0 ? <tr><td colSpan="5"><Empty message="No active projects found" /></td></tr> :
                   searchedProjects.map((p, i) => (
                     <tr key={p.project_id}>
                       <td style={{ color: 'var(--text-muted)' }}>{p.project_code || '—'}</td>
                       <td>
                         <div className="dashboard-modern__table-name">
                            <div className="dashboard-modern__table-avatar" style={{ background: projectAccentColors[i%projectAccentColors.length] + '22', color: projectAccentColors[i%projectAccentColors.length] }}>
                             {p.project_name.charAt(0)}
                           </div>
                           <div>
                             <div className="dashboard-modern__table-title">{p.project_name}</div>
                             <div className="dashboard-modern__table-sub">Due {formatDate(p.end_date) || '—'}</div>
                           </div>
                         </div>
                       </td>
                       <td style={{ color: 'var(--text-dim)' }}>{p.team || 'Unassigned'}</td>
                       <td>
                         <div className="dashboard-modern__status-badge">
                            <span className="dashboard-modern__status-dot" style={{ background: statusColor[p.status] || 'var(--accent)' }} />
                            {p.status}
                         </div>
                       </td>
                       <td>
                         <button className="dashboard-modern__action-btn" onClick={() => navigate('/app/projects')}>Details</button>
                       </td>
                     </tr>
                   ))
                 }
               </tbody>
             </table>
          </div>
        </div>

        {/* My Tasks List */}
        <div className="dashboard-modern__panel">
          <div className="dashboard-modern__panel-header">
            <h2 className="dashboard-modern__panel-title">My Tasks</h2>
          </div>
          <div className="dashboard-modern__task-list">
             {loading ? <Skeleton height={150} /> : openMyTasks.length === 0 ? <Empty message="No open tasks" /> : 
                openMyTasks.map((task, i) => (
                  <div key={task.task_id} className={`dashboard-modern__task-item ${i === 0 ? 'dashboard-modern__task-item--active' : ''}`}>
                    <div className="dashboard-modern__task-num">{i + 1}</div>
                    <div className="dashboard-modern__task-icon">📋</div>
                    <div className="dashboard-modern__task-info">
                       <div className="dashboard-modern__task-name">{task.title || task.task_name}</div>
                       <div className="dashboard-modern__task-meta">{task.priority || 'Medium'} Priority • Due {formatDate(task.due_date) || '—'}</div>
                    </div>
                    <div style={{ position: 'relative' }}>
                      <button className="dashboard-modern__more-btn" onClick={() => toggleMenu(`task-${task.task_id}`)}>⋮</button>
                      {activeMenu === `task-${task.task_id}` && (
                        <div className="dashboard-modern__dropdown dashboard-modern__dropdown--right">
                          <div className="dashboard-modern__dropdown-item" onClick={() => navigate('/app/tasks')}>Go to Tasks</div>
                          <div className="dashboard-modern__dropdown-item">Quick Edit</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))
             }
          </div>
        </div>

        {/* ✨ AI Assistant Widget – Dark theme */}
        <div className="dashboard-modern__panel" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Header */}
          <div style={{
            padding: '14px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            borderBottom: '1px solid var(--dashboard-border)',
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: '8px',
              background: 'var(--accent-surface, rgba(139,132,255,0.15))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16,
            }}>✨</div>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'var(--dashboard-text)', fontWeight: 700, fontSize: 13.5, lineHeight: 1.2 }}>AI Assistant</div>
              <div style={{ color: 'var(--dashboard-dim)', fontSize: 11 }}>Powered by your local library</div>
            </div>
            {chatMessages.length > 0 && (
              <button
                type="button"
                onClick={() => { setChatMessages([]); setChatError(''); }}
                style={{
                  background: 'var(--dashboard-panel-2)', border: '1px solid var(--dashboard-border-soft)',
                  color: 'var(--dashboard-dim)', borderRadius: '6px',
                  cursor: 'pointer', fontSize: 14, padding: '4px 8px', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', fontWeight: 500
                }}
                title="Back to suggested questions"
              >
                Back
              </button>
            )}
          </div>

          {/* Body */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: '14px',
            display: 'flex', flexDirection: 'column', gap: '10px',
            background: 'var(--dashboard-panel)',
            minHeight: 160, maxHeight: 220,
          }}>
            {chatMessages.length === 0 ? (
              <>
                <p style={{ textAlign: 'center', color: 'var(--dashboard-text)', fontWeight: 700, fontSize: 14, margin: '12px 0 4px' }}>
                  Hi {user?.name ? user.name.split(' ')[0] : 'there'} 👋
                </p>
                <p style={{ textAlign: 'center', color: 'var(--dashboard-dim)', fontSize: 12, margin: '0 0 10px' }}>
                  Ask about your projects, tasks, or team members.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center' }}>
                  {chatSuggestions.map((s) => (
                    <button
                      key={s} type="button" onClick={() => handleChatSubmit(null, s)}
                      style={{
                        fontSize: 11.5, padding: '5px 11px', borderRadius: 999,
                        border: '1px solid var(--dashboard-border-soft)',
                        background: 'var(--dashboard-panel-2)',
                        color: 'var(--dashboard-dim)', cursor: 'pointer', fontWeight: 500,
                      }}
                    >{s}</button>
                  ))}
                </div>
              </>
            ) : (
              chatMessages.map((msg, i) => (
                <div key={i} style={{
                  alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                  background: msg.sender === 'user' ? 'var(--accent)' : 'var(--dashboard-panel-2)',
                  color: msg.sender === 'user' ? '#fff' : 'var(--dashboard-text)',
                  padding: '9px 14px',
                  borderRadius: msg.sender === 'user' ? '14px 14px 0 14px' : '14px 14px 14px 0',
                  maxWidth: '85%', fontSize: 13, lineHeight: 1.45,
                  border: msg.sender === 'ai' ? '1px solid var(--dashboard-border-soft)' : 'none',
                  wordBreak: 'break-word', whiteSpace: 'pre-wrap',
                }}>{msg.text}</div>
              ))
            )}
            {chatLoading && (
              <div style={{
                alignSelf: 'flex-start', display: 'flex', gap: 4,
                padding: '8px 14px', background: 'var(--dashboard-panel-2)',
                borderRadius: '14px 14px 14px 0',
                border: '1px solid var(--dashboard-border-soft)',
              }}>
                {[0,1,2].map(i => (
                  <span key={i} style={{
                    width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)',
                    display: 'inline-block',
                    animation: `bounce 1.2s ${i*0.2}s infinite`,
                  }} />
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={handleChatSubmit} style={{
            display: 'flex', gap: 8, padding: '12px 14px',
            borderTop: '1px solid var(--dashboard-border)',
            background: 'var(--dashboard-panel)',
          }}>
            <input
              type="text"
              placeholder="Ask me anything..."
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              style={{
                flex: 1, padding: '8px 13px', borderRadius: 8,
                border: '1px solid var(--dashboard-border-soft)',
                background: 'var(--dashboard-panel-2)',
                fontSize: 12.5, color: 'var(--dashboard-text)', outline: 'none',
                fontFamily: 'inherit',
              }}
            />
            <button
              type="submit"
              disabled={chatLoading || !chatInput.trim()}
              style={{
                width: 36, height: 36, borderRadius: 8, border: 'none',
                background: 'var(--accent)',
                color: '#fff', fontSize: 15, cursor: 'pointer',
                opacity: chatLoading || !chatInput.trim() ? 0.45 : 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, transition: 'opacity 0.2s',
              }}
            >➔</button>
          </form>
        </div>
      </section>

      {error && <div className="dashboard-modern__error">{error}</div>}
    </div>
  );
}